import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { segment } from '@actions/analytics';
import Paper from '@base/Paper/Paper';
import { breakpoints, Fonts, Spacings } from '@styles/index';
import H2 from '@base/H2/H2';
import ChevronRight from '@icon/ChevronRight';
import CMSHTMLContent from '@comp/CMSHTMLContent/CMSHTMLContent';
import { usePageSeoContent } from '@stores/AppCommonData';
import ContentDrawer from './ContentDrawer';

const Container = styled(Paper)`
  width: ${({ $width }) => `${$width}`};
  padding: ${Spacings.SPACING_22B} ${Spacings.SPACING_20B};
  margin: ${40 * Spacings.BASE}px auto;
  position: relative;
  min-height: 288px;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 728px;
    padding: ${Spacings.SPACING_22B} ${Spacings.SPACING_15B};
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
    margin: ${Spacings.SPACING_20B} auto 0;
    box-shadow: none;
    background: var(--color-base-0);
    padding: 0 ${Spacings.SPACING_4B};
  }
`;

const Title = styled(H2)`
  font-size: ${Fonts.H3};
  width: 460px;
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  margin-bottom: ${Spacings.SPACING_4B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 232px;
    font-size: 22px;
    line-height: ${Fonts.HEADER_LINE_HEIGHT};
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
    font-size: 22px;
    margin-bottom: ${Spacings.SPACING_2B};
  }
`;

const SubTitle = styled(H2)`
  font-size: ${Fonts.LARGE};
  width: 355px;
  &.light-bold {
    font-weight: 600;
  }
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 232px;
    font-size: 14px;
    line-height: ${Fonts.HEADER_LINE_HEIGHT};
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
    font-size: ${Fonts.SMALL};
  }
`;

const CardsContainer = styled.div`
  position: absolute;
  right: ${Spacings.SPACING_24B};
  top: -${Spacings.SPACING_10B};
  width: 416px;
  & div:last-child {
    margin-bottom: 0;
  }
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 296px;
    right: ${Spacings.SPACING_15B};
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    position: relative;
    width: 100%;
    top: initial;
    right: initial;
    display: grid;
    grid-gap: ${Spacings.SPACING_3B};
    grid-template-columns: 1fr 1fr;
    margin-top: ${Spacings.SPACING_6B};
  }
`;

const Card = styled(Paper)`
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_6B};
  display: flex;
  align-items: center;
  margin-bottom: ${Spacings.SPACING_4B};
  cursor: pointer;
  @media only screen and (min-width: ${breakpoints.tablet}) {
    min-height: 80px;
  }
  @media only screen and (max-width: ${breakpoints.tablet}) {
    padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-bottom: 0;
    flex-direction: column;
    align-items: flex-start;
    padding: ${Spacings.SPACING_4B};
  }
`;

const Image = styled.img`
  user-select: none;
  margin-right: ${Spacings.SPACING_4B};
  width: 56px;
  height: 56px;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    margin-right: ${Spacings.SPACING_2B};
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 48px;
    height: 48px;
    margin: 0 0 ${Spacings.SPACING_4B} 0;
  }
`;

const StyledChevronRight = styled(ChevronRight)`
  @media only screen and (max-width: ${breakpoints.mobile}) {
    max-width: 0;
    max-height: 0;
  }
`;

const Cards = ({ cards, setDrawerState }) => {
  const handleClick = (card) => {
    setDrawerState(card.actionText);
    segment.track('Onboarding - Goal Content Block Clicked', {
      is_logged_in: false,
      goal_content_block_type: card.description
    });
  };
  return (
    <CardsContainer>
      {cards?.length > 0 &&
        cards.map((card) => (
          <Card key={card.id} onClick={() => handleClick(card)}>
            <Image src={card.image} alt={card.title} />
            <SubTitle className="light-bold">{card.title}</SubTitle>
            <StyledChevronRight size="20" color="var(--color-text-primary)" />
          </Card>
        ))}
    </CardsContainer>
  );
};

Cards.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.any).isRequired,
  setDrawerState: PropTypes.func.isRequired
};

export default function GoalCmsContent({ width }) {
  const [drawerState, setDrawerState] = React.useState(false);
  const { contentBlock } = usePageSeoContent();
  if (!contentBlock?.title) return null;
  const { title, subTitle, blocks, curtainContent } = contentBlock;
  const closeModal = () => setDrawerState(false);
  const callback = () => {
    document.getElementById(drawerState)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return (
    <>
      <Container $width={width}>
        <Title>{title}</Title>
        <SubTitle>{subTitle}</SubTitle>
        {blocks?.length > 0 && (
          <Cards cards={blocks} setDrawerState={setDrawerState} />
        )}
      </Container>
      <ContentDrawer
        open={!!drawerState}
        onClose={closeModal}
        callback={callback}
      >
        <CMSHTMLContent content={curtainContent} />
      </ContentDrawer>
    </>
  );
}

GoalCmsContent.propTypes = {
  width: PropTypes.string
};
GoalCmsContent.defaultProps = {
  width: '1136px'
};
